/**
 * 选择交互：点选 / Shift 加选 / 空白取消 / 框选 / 双击进组。
 * 只改「选中集」与「视图平移」，不碰文档。
 *
 * 与变换交互的分工：本文件负责"选中什么"，`interactions/transform.ts` 负责"拖动它"。
 * 两者都挂在宿主上；变换交互用捕获阶段先跑，本文件用冒泡阶段收尾。
 */
import { isEditable } from '@/core/dom';
import { apply, type Rect } from '@/core/geom/matrix';
import { bboxInRoot, rectsIntersect, screenToRootMat } from '@/core/geom/viewport';
import { frameFromRects, type Frame } from '../overlay';

export type SelectMode = 'replace' | 'add' | 'toggle';

export interface PointerDeps {
  host: HTMLElement;
  getSvg(): SVGSVGElement | null;
  getSelection(): readonly Element[];
  setSelection(els: Element[], mode: SelectMode): void;
  panBy(dx: number, dy: number): void;
  isPanMode(): boolean;
  setMarquee(frame: Frame | null): void;
  onEmptyClick?(): void;
  /** 双击进入的分组上下文 */
  getContext(): Element | null;
  setContext(ctx: Element | null): void;
  /** 双击命中文本 → 就地进行文本编辑；返回 true 表示已接管 */
  beginTextEdit?(el: Element): boolean;
  /** 正在用绘制工具（此时不做选择/框选） */
  isDrawing?(): boolean;
  /** 该元素此刻是否允许被点选（锁定元素在画布上不可选，但仍可从列表选中） */
  isSelectable?(el: Element): boolean;
}

/**
 * 命中语义：
 *  · 无上下文 → 最外层可编辑祖先（点组里任一元素都选到组）
 *  · 有上下文 → 上下文的**直接子元素**（进组后逐个选）
 */
export /**
 * 单击命中：取指针下**最具体**的那个可编辑元素 —— 「你指着谁就选谁」。
 *
 * 曾经这里是"向上找最外层可编辑祖先"（= Figma 的单击选顶层容器语义），
 * 结果与参考产品相反：点组里的一段文字会选中**整个组**（框住整块画板），
 * 用户觉得"很难选中"。现在单击 = 点到的那个元素；要选外层组用 **Alt+单击**（见 resolveOutermost）。
 */
function resolveSelectable(
  target: EventTarget | null,
  svg: SVGSVGElement,
  context: Element | null,
  isSelectable?: (el: Element) => boolean,
): Element | null {
  if (context) {
    let node = target as Element | null;
    while (node && node !== svg && node.parentElement !== context) node = node.parentElement;
    return node && node !== svg && context.contains(node) ? node : context;
  }
  let el = target as Element | null;
  let hit: Element | null = null;
  // 只在**本 svg 子树内**向上找：越过边界（宿主 div / body / html）一律不算命中
  while (el && el !== svg && svg.contains(el)) {
    // 第一个（最内层）可编辑元素就是命中：<tspan> 等非可编辑节点会被跳过，落到它的 <text>
    if (isEditable(el)) {
      hit = el;
      break;
    }
    el = el.parentElement;
  }
  if (hit && isSelectable && !isSelectable(hit)) return null; // 锁定 → 当空白
  return hit;
}

/** 取指针下**最外层**的可编辑祖先（Alt+单击用：一次选中整个组） */
export function resolveOutermost(
  target: EventTarget | null,
  svg: SVGSVGElement,
  isSelectable?: (el: Element) => boolean,
): Element | null {
  let el = target as Element | null;
  let outermost: Element | null = null;
  while (el && el !== svg && svg.contains(el)) {
    if (isEditable(el)) outermost = el;
    el = el.parentElement;
  }
  if (outermost && isSelectable && !isSelectable(outermost)) return null;
  return outermost;
}

/** 取指针下**最深**的可编辑元素（双击进组用；无 elementsFromPoint 的环境返回 null） */
export function deepestSelectable(
  x: number,
  y: number,
  svg: SVGSVGElement,
  context: Element | null,
): Element | null {
  if (typeof document.elementsFromPoint !== 'function') return null;
  const root: Element = svg;
  for (const node of document.elementsFromPoint(x, y)) {
    if (node === root || !svg.contains(node) || !isEditable(node)) continue;
    if (context && !context.contains(node)) continue;
    return node;
  }
  return null;
}

const fromOverlay = (target: EventTarget | null): boolean =>
  Boolean((target as Element | null)?.closest?.('.sve-overlay'));

export function attachPointerInteraction(deps: PointerDeps): () => void {
  const { host } = deps;
  let dragging: { startX: number; startY: number; additive: boolean; panning: boolean } | null = null;

  const localFrame = (x0: number, y0: number, x1: number, y1: number): Frame => {
    const hostRect = host.getBoundingClientRect();
    return frameFromRects(
      new DOMRect(
        hostRect.left + Math.min(x0, x1),
        hostRect.top + Math.min(y0, y1),
        Math.abs(x1 - x0),
        Math.abs(y1 - y0),
      ),
      hostRect,
    );
  };

  const onDown = (e: PointerEvent): void => {
    const svg = deps.getSvg();
    if (!svg) return;
    if (fromOverlay(e.target)) return; // 手柄/覆盖层交给变换交互，别当空白处理
    if (deps.isDrawing?.()) return; // 绘制中：让路
    const hostRect = host.getBoundingClientRect();
    const x0 = e.clientX - hostRect.left;
    const y0 = e.clientY - hostRect.top;
    const panning = deps.isPanMode() || e.button === 1;
    const additive = e.shiftKey;

    if (!panning && e.button === 0) {
      // 常规单击 = 点到的那个元素；Alt+单击 = 它所属的最外层组（选整组不用去左栏找）
      const hit = e.altKey
        ? (resolveOutermost(e.target, svg, deps.isSelectable) ?? resolveSelectable(e.target, svg, deps.getContext(), deps.isSelectable))
        : resolveSelectable(e.target, svg, deps.getContext(), deps.isSelectable);
      if (hit) {
        const already = deps.getSelection().includes(hit);
        if (additive) deps.setSelection([hit], already ? 'toggle' : 'add');
        else if (!already) deps.setSelection([hit], 'replace');
        return; // 命中元素：拖动交给变换交互
      }
      if (!additive) deps.onEmptyClick?.();
    }

    dragging = { startX: x0, startY: y0, additive, panning };
    try {
      host.setPointerCapture?.(e.pointerId);
    } catch {
      /* 指针捕获只是优化，失效不影响选择逻辑 */
    }
  };

  const onMove = (e: PointerEvent): void => {
    if (!dragging) return;
    const hostRect = host.getBoundingClientRect();
    const x = e.clientX - hostRect.left;
    const y = e.clientY - hostRect.top;
    if (dragging.panning) {
      deps.panBy(e.movementX || x - dragging.startX, e.movementY || y - dragging.startY);
      dragging.startX = x;
      dragging.startY = y;
      return;
    }
    deps.setMarquee(localFrame(dragging.startX, dragging.startY, x, y));
  };

  const onUp = (e: PointerEvent): void => {
    const svg = deps.getSvg();
    if (!dragging || !svg) {
      dragging = null;
      return;
    }
    const hostRect = host.getBoundingClientRect();
    const x = e.clientX - hostRect.left;
    const y = e.clientY - hostRect.top;
    const moved = Math.abs(x - dragging.startX) > 3 || Math.abs(y - dragging.startY) > 3;
    if (!dragging.panning && moved) {
      const hits = selectInRect(svg, host, localFrame(dragging.startX, dragging.startY, x, y), deps.getContext());
      deps.setSelection(hits, dragging.additive ? 'add' : 'replace');
    }
    deps.setMarquee(null);
    dragging = null;
    try {
      host.releasePointerCapture?.(e.pointerId);
    } catch {
      /* 同上 */
    }
  };

  const onDblClick = (e: MouseEvent): void => {
    const svg = deps.getSvg();
    if (!svg) return;
    const context = deps.getContext();
    const hit = resolveSelectable(e.target, svg, context);
    const deepest = deepestSelectable(e.clientX, e.clientY, svg, context) ?? hit;
    if (!deepest || deepest === context) return;
    if (deps.beginTextEdit?.(deepest)) return; // 双击文本 = 就地编辑

    const root: Element = svg;
    const parent: Element | null = deepest.parentElement;
    if (parent && parent !== root && parent.tagName.toLowerCase() === 'g' && parent !== context) {
      deps.setContext(parent); // 进组：之后点击只在组内选择
    }
    deps.setSelection([deepest], 'replace');
  };

  host.addEventListener('pointerdown', onDown);
  host.addEventListener('pointermove', onMove);
  host.addEventListener('pointerup', onUp);
  host.addEventListener('pointercancel', onUp);
  host.addEventListener('dblclick', onDblClick);
  return () => {
    host.removeEventListener('pointerdown', onDown);
    host.removeEventListener('pointermove', onMove);
    host.removeEventListener('pointerup', onUp);
    host.removeEventListener('pointercancel', onUp);
    host.removeEventListener('dblclick', onDblClick);
  };
}

/** 框选：屏幕矩形 → 根用户坐标，与元素包围盒几何相交；只在当前上下文内命中 */
export function selectInRect(
  svg: SVGSVGElement,
  host: HTMLElement,
  frame: Frame,
  context: Element | null = null,
): Element[] {
  const hostRect = host.getBoundingClientRect();
  const toRoot = screenToRootMat(svg);
  if (!toRoot) return [];
  const p1 = apply(toRoot, { x: hostRect.left + frame.left, y: hostRect.top + frame.top });
  const p2 = apply(toRoot, {
    x: hostRect.left + frame.left + frame.width,
    y: hostRect.top + frame.top + frame.height,
  });
  const box: Rect = {
    x: Math.min(p1.x, p2.x),
    y: Math.min(p1.y, p2.y),
    w: Math.abs(p2.x - p1.x),
    h: Math.abs(p2.y - p1.y),
  };

  const candidates = context ? Array.from(context.children) : Array.from(svg.querySelectorAll('*').values());
  const hits: Element[] = [];
  for (const el of candidates) {
    if (!isEditable(el)) continue;
    if (context && !context.contains(el)) continue;
    const b = bboxInRoot(el as SVGGraphicsElement, svg);
    if (b && rectsIntersect(box, b)) hits.push(el);
  }
  return hits.filter((el) => !hits.some((other) => other !== el && other.contains(el)));
}
